import "./style.css";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { gsap } from "gsap";
import { TweenMax } from "gsap/gsap-core";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Reflector } from "three/examples/jsm/objects/Reflector";
import { Raycaster } from "three";

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.defaults({ scroller: ".main" });

// Colors
const COLORS = {
  background: "black",
  light: "#ffffff",
  ground: "#ffffff",
  blue: "steelblue",
};

/**
 * Loaders
 */
const loadingBarElement = document.querySelector(".loading-bar");
const loadingTextElement = document.querySelector(".loading-text");
const pageElement = document.querySelector(".page");
const mainElement = document.querySelector(".main");
/**
 * Base
 */
// Debug
const debugObject = {};

// Canvas
const canvas = document.querySelector("canvas.webgl");

var textureLoader = new THREE.TextureLoader();

// Wireframe material
const wireframeMaterial = new THREE.MeshBasicMaterial({
  color: "white",
  wireframe: true,
});

// Scene
const scenes = {
  real: new THREE.Scene(),
  wireframe: new THREE.Scene(),
};

scenes.wireframe.overrideMaterial = wireframeMaterial;

scenes.real.background = new THREE.Color(COLORS.background);
scenes.wireframe.background = new THREE.Color(COLORS.blue);

const views = [
  { height: 1, bottom: 0, scene: scenes.real, camera: null },
  { height: 0, bottom: 0, scene: scenes.wireframe, camera: null },
];

/**
 * Overlay
 */

const overlayGeometry = new THREE.PlaneGeometry(2, 2, 1, 1);
const overlayMaterial = new THREE.ShaderMaterial({
  transparent: true,
  uniforms: {
    uAlpha: { value: 1 },
  },
  vertexShader: `
    void main()
    {
        gl_Position = vec4(position, 1.0);
    }
    `,

  fragmentShader: `
    uniform float uAlpha;
    void main()
    {
        gl_FragColor = vec4(0.0, 0.0, 0.0 , uAlpha);
    }
    `,
});
const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial);
// scenes.real.add(overlay);

/**
 * Textures
 */

// Carbon fibre
const carbonFibreBaseColor = textureLoader.load(
  "/models/teslaCompressed/textures/carbon_fibre_baseColor.jpeg"
);
const carbonFibreRoughntness = textureLoader.load(
  "/models/teslaCompressed/textures/carbon_fibre_metallicRoughness.png"
);
const carbonFibreNormal = textureLoader.load(
  "/models/teslaCompressed/textures/carbon_fibre_normal.jpeg"
);

// Grill
const grillBaseColor = textureLoader.load(
  "/models/teslaCompressed/textures/grill_baseColor.png"
);
const grillNormal = textureLoader.load(
  "/models/teslaCompressed/textures/grill_normal.png"
);

/**
 * Lights
 */
const directionalLight = new THREE.DirectionalLight("#ffffff", 2);
directionalLight.castShadow = true;
directionalLight.shadow.camera.far = 10;
directionalLight.shadow.mapSize.set(1024, 1024);
directionalLight.shadow.normalBias = 0.05;
directionalLight.position.set(2, 5, 3);
scenes.real.add(directionalLight);

// HemisphereLight
const hemisphereLight = new THREE.HemisphereLight(
  COLORS.sky,
  COLORS.ground,
  0.5
);
scenes.real.add(hemisphereLight);

/**
 * Floor
 */
const plane = new THREE.PlaneGeometry(100, 100);
const groundMirror = new Reflector(plane, {
  clipBias: 0.0000001,
  textureWidth: window.innerWidth * window.devicePixelRatio,
  textureHeight: window.innerHeight * window.devicePixelRatio,
  color: "0xffffff",
  recursion: 1,
  multisample: 4,
});

groundMirror.rotateX(-Math.PI * 0.5);
scenes.real.add(groundMirror);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  views.forEach((view) => {
    view.camera.aspect = sizes.width / sizes.height;
    view.camera.updateProjectionMatrix();
  });

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera

let cameraTarget = new THREE.Vector3(0, 0, 0);

views.forEach((view) => {
  view.camera = new THREE.PerspectiveCamera(
    75,
    sizes.width / sizes.height,
    0.1,
    100
  );
  view.camera.position.set(0, 1, 4);
  view.scene.add(view.camera);
});

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
  alpha: true,
});
renderer.physicallyCorrectLights = true;
renderer.outputEncoding = THREE.sRGBEncoding;
renderer.toneMapping = THREE.ReinhardToneMapping;
renderer.toneMappingExposure = 3;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

// Controls

const controls = new OrbitControls(views[0].camera, renderer.domElement);
controls.enableDamping = true;
// controls.enableZoom = false
controls.enablePan = false;
controls.maxPolarAngle = Math.PI / 2.3;
controls.maxDistance = 6;
controls.minDistance = 3;

// Points

const raycaster = new Raycaster();

const points = [
  {
    position: new THREE.Vector3(-0.65, 0.9, 1.2),
    element: document.querySelector(".point-0"),
  },
  {
    position: new THREE.Vector3(-0.2, 1.1, -0.45),
    element: document.querySelector(".point-1"),
  },
  {
    position: new THREE.Vector3(0.5, 0.5, 1.3),
    element: document.querySelector(".point-2"),
  },
  {
    position: new THREE.Vector3(2, 0.7, 0.23),
    element: document.querySelector(".point-3"),
  },
];

/**
 * Animate
 */

const toLoad = [
  {
    name: "tesla",
    group: new THREE.Group(),
    file: "/models/teslaCompressed/tesla.glb",
  },
];

const models = {};
const clones = {};

// Animations

let cameras = null;
let teslas = null;

const setUpAnimation = () => {
  cameras = { position: [views[0].camera.position, views[1].camera.position] };
  teslas = {
    position: [models.tesla.position, clones.tesla.position],
    rotation: [models.tesla.rotation, clones.tesla.rotation],
  };

  ScrollTrigger.matchMedia({
    "(prefers-reduced-motiom: no-preference)": desktopAnimation(),
  });
  //   gsap.set(teslas.position, {x: 0})
  gsap.set(teslas.rotation, { y: Math.PI / 5 });
};

const desktopAnimation = () => {
  let section = 0;
  const tl = gsap.timeline({
    default: {
      duration: 1,
      ease: "power2.inOut",
    },
    scrollTrigger: {
      trigger: ".page",
      start: "top top",
      end: "bottom top",
      scrub: 0.1,
    },
  });

  //   Title Section
  tl.to(teslas.position, { x: 0, ease: "power2.in" }, section);
  tl.to(cameraTarget, { y: 1 }, section);
  tl.to(cameras.position, { z: 3.5, ease: "power2.in" }, section);
  tl.to(teslas.rotation, { y: 0, ease: "power2.in", duration: 0.8 }, section);


  // Section 2
  section += 1;
  tl.to(teslas.position, { x: 0, ease: "power4.in" }, section);
  tl.to(
    teslas.rotation,
    { y: Math.PI / 2, ease: "power4.in", duration: 1 },
    section
  );

  // Section 3
  section += 1;
  tl.to(teslas.position, { x: 0, ease: "power4.in" }, section);
  tl.to(
    teslas.rotation,
    { y: Math.PI / 0.5, ease: "slow(0.7, 0.7, false)", duration: 0.8 },
    section
  );

  // Section 4
  section += 1;
  tl.to(cameras.position, { z: 4.5, ease: "power2.in" }, section);
  tl.to(
    teslas.position,
    { x: 0, z: -1, ease: "slow(0.7, 0.7, false)" },
    section
  );
  tl.to(
    teslas.rotation,
    { z: Math.PI * 2, ease: "none", duration: 1 },
    section
  );
  tl.to(views[1], { height: 1, ease: "none" }, section);

  // Section 5
  section += 1;
  tl.to(teslas.position, { z: 0.3, ease: "power4.out" }, section);
  tl.to(
    teslas.rotation,
    { y: Math.PI * 0.2, ease: "slow(0.7, 0.7, false)", duration: 1 },
    section
  );
  tl.to(views[1], { height: 0, bottom: 1, ease: "none" }, section);
  tl.to(
    ".finalButton",
    { ease: "power4.in", css: { zIndex: "3", display: "inline-block" } },
    section
  );
  tl.to(
    ".footer",
    { ease: "power4.in", css: { zIndex: "3", display: "flex" } },
    section
  );
  tl.to(
    ".colors",
    { ease: "power4.in", css: { zIndex: "3", display: "flex" } },
    section
  );
  // tl.to(
  //   ".point",
  //   { ease: "power4.in", css: { zIndex: "3", display: "flex" } },
  //   section
  // );
  tl.to(".webgl", { css: { zIndex: "2" } }, section);

  // Section 6
  section += 1;
  tl.to(teslas.rotation, { y: -0.5 }, section);
  tl.to(teslas.position, { x: 1.5, z: 0, ease: "power4.in" }, section);
};

//  TEXTS
function firstTexts() {
  TweenMax.staggerFrom(".text-left", 1, {
    opacity: 0,
    x: -200,
    ease: "slow (0.7, 0.7, false)",
    duration: 3,
    delay: 1.5,
  });

  TweenMax.staggerFrom(".text-right", 1, {
    opacity: 0,
    x: 200,
    duration: 2,
    ease: "slow (0.7, 0.7, false)",
    delay: 3.5,
  });
}

// Section 1
const section1Tl = gsap.timeline({
  defaults: {
    ease: "power4.out",
  },
  scrollTrigger: {
    trigger: ".section1",
    start: "bottom bottom",
    end: "bottom top",
    toggleActions: "play reset reset reset",
    scrub: true,
  },
});
section1Tl.to(".scrollDown", { y: -150, opacity: 0, duration: 4 }, "-=4");

// Section 2
const section2Tl = gsap.timeline({
  defaults: {
    ease: "power4.out",
  },
  scrollTrigger: {
    trigger: ".section2",
    start: "center bottom",
    end: () => document.querySelector("body").offsetHeight / 1.6 + "center",
    toggleActions: "play reset reset reset",
    scrub: true,
  },
});

section2Tl.from(".stat1", { y: 50, opacity: 0, duration: 4 }, "-=4");
section2Tl.from(".stat2", { y: 100, opacity: 0, duration: 4 }, "-=4");
section2Tl.from(".stat3", { y: 75, opacity: 0, duration: 4 }, "-=4");

// Section 3
const section3Tl = gsap.timeline({
  defaults: {
    ease: "power4.out",
  },
  scrollTrigger: {
    trigger: ".section3",
    start: "center bottom",
    end: () => document.querySelector("body").offsetHeight / 2 + "center",
    toggleActions: "play reset reset reset",
    scrub: true,
  },
});

section3Tl.from(".first-row", { y: 100, opacity: 0, duration: 0.8 });
section3Tl.from(".second-row", { y: 110, opacity: 0, duration: 1 });
section3Tl.from(".third-row", { y: 120, opacity: 0, duration: 1.2 });
section3Tl.from(".fourth-row", { y: 130, opacity: 0, duration: 1.4 });
section3Tl.from(".fivth-row", { y: 140, opacity: 0, duration: 1.6 });
section3Tl.from(".six-row", { y: 150, opacity: 0, duration: 1.7 });
section3Tl.from(".seven-row", { y: 160, opacity: 0, duration: 1.8 });
section3Tl.from(".eight-row", { y: 170, opacity: 0, duration: 1.9 });
section3Tl.from(".nine-row", { y: 180, opacity: 0, duration: 2 });

// Section 4

const section4Tl = gsap.timeline({
  defaults: {
    ease: "power4.out",
  },
  scrollTrigger: {
    trigger: ".section4",
    start: "center bottom",
    end: () => document.querySelector("body").offsetHeight / 1.5 + "center",
    toggleActions: "play reset reset reset",
  },
});

section4Tl.from(".button", { y: 50, opacity: 0, duration: 5 }, "-=5");

// Section 5
const section5Tl = gsap.timeline({
  defaults: {
    ease: "power4.out",
  },
  scrollTrigger: {
    trigger: ".section5",
    start: "center center",
    end: () => document.querySelector("body").offsetHeight / 3 + "center",
    toggleActions: "play reset reset reset",
    scrub: true,
  },
});

section5Tl.to(".hiddenText", { y: "0%", duration: 0.7, stagger: 0.2 }, "-=0.2");

// Section 6
const section6Tl = gsap.timeline({
  defaults: {
    ease: "power4.out",
  },
  scrollTrigger: {
    trigger: ".section5",
    start: () => document.querySelector("body").offsetHeight / 1.3 + "top",
    end: "200% top",
    toggleActions: "play reset reset reset",
  },
});

section6Tl.from(".footerText1", {
  x: -180,
  opacity: 0,
  duration: 1.2,
  delay: 0.5,
});
section6Tl.from(".footerText2", {
  x: 180,
  opacity: 0,
  duration: 1.2,
  delay: 0.7,
});
section6Tl.from(".colors", { x: 180, opacity: 0, duration: 1.2});
section6Tl.from(".buttonToTop", {
  y: -180,
  opacity: 0,
  duration: 1,
  delay: 1,
});
// section6Tl.from(".point", { x: 180, opacity: 0, duration: 1.2});

// Animate random dado
let targets = gsap.utils.toArray(".bx-dice-6");

targets.forEach((obj) => {
  obj.anim = gsap.to(obj, { rotation: 360 * 5, duration: 1 }).reversed(true);
  obj.addEventListener("click", doCoolStuff);
});

function doCoolStuff() {
  this.anim.reversed(!this.anim.reversed());
}

// Display/not the points
console.clear();
let pointss = gsap.utils.toArray(".point");
let see = document.querySelector('.see')

function hide() {
pointss.forEach((obj) => {
  if (obj.style.opacity== 1){
    obj.anim = gsap.to(obj, {opacity: 0, zIndex: 0, duration: 1})
  }
  else
  {
    obj.anim = gsap.to(obj, {opacity: 1, zIndex: 3, duration: 1})
  }
});

}
see.addEventListener("click", hide);


const loadingManager = new THREE.LoadingManager(
  () => {
    // Loaded
    firstTexts();
    setUpAnimation();
    gsap.delayedCall(0.5, () => {
      gsap.to(overlayMaterial.uniforms.uAlpha, { duration: 3, value: 0 });
      loadingBarElement.classList.add("ended");
      loadingTextElement.classList.add("ended");
      pageElement.classList.add("ended");
      
      gsap.to(canvas, {
        css: { display: "block", opacity: "1" },
        duration: 3,
        delay: 3,
      });
      
      canvas.classList.add("ended");
      loadingBarElement.style.transform = "";
      loadingTextElement.style.transform = "";
      
      gsap.delayedCall(4.5, () => {
        gsap.to(".scrollDown", { css: { opacity: "0.5" } });
        mainElement.classList.add("ended");
      })
    });
  },

  // Progress
  (itemUrl, itemLoaded, itemsTotal) => {
    const progressRatio = itemLoaded / itemsTotal;
    loadingBarElement.style.transform = `scaleX(${progressRatio})`;
  }
);

const gltfLoader = new GLTFLoader(loadingManager);
const dracoLoader = new DRACOLoader();
gltfLoader.setDRACOLoader(dracoLoader);
dracoLoader.setDecoderPath("/draco/");

const colorMaterials = [];
let r;
let g;
let b;

let blackButton = document.querySelector(".black");
let whiteButton = document.querySelector(".white");
let orangeButton = document.querySelector(".orange");
let silverButton = document.querySelector(".silver");
let blueButton = document.querySelector(".blue");
let pinkButton = document.querySelector(".pink");

const colors = { r, g, b };

toLoad.forEach((item) => {
  gltfLoader.load(item.file, (model) => {
    model.scene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.material.envMapIntensity = debugObject.envMapIntensity;
        child.material.needsUpdate = true;
        child.castShadow = true;
        child.receiveShadow = true;

        //  Carbon Fibre Textures
        if (child.material.name == "carbon_fibre") {
          child.material.roughnessMap = carbonFibreRoughntness;
          child.material.normalMap = carbonFibreNormal;
          child.material.map = carbonFibreBaseColor;
        }
        // //  grill Textures
        if (child.material.name == "grill") {
          child.material.normalMap = grillNormal;
          child.material.map = grillBaseColor;
        }
        //  Sidewalls Textures
        // if (child.material.name == 'Sidewall'){
        //     child.material.normalMap = sidewallNormal
        //     child.material.map = sidewallBaseColor
        // }
        //  Thread Textures
        // if (child.material.name == 'Thread'){
        //     child.material.normalMap = threadNormal
        //     child.material.map = threadBaseColor
        // }

        // child.material.envMap = enviromentMap
        if (child.material.name == "car_main_paint") {
          colorMaterials.push(child);
        }
      }
    });
    item.group.add(model.scene);
    scenes.real.add(item.group);
    models[item.name] = item.group;

    // Clones
    const clone = item.group.clone();
    clones[item.name] = clone;
    scenes.wireframe.add(clone);



    // Change Colors
    function updateMaterials() {
      colorMaterials[5].material.color.r = colors.r;
      colorMaterials[5].material.color.g = colors.g;
      colorMaterials[5].material.color.b = colors.b;
      colorMaterials[5].material.needsUpdate = true;
    }

    // Black
    blackButton.addEventListener("click", () => {
      colors.b = 0.025;
      colors.r = 0.024;
      colors.g = 0.023;
      updateMaterials();
    });

    // White
    whiteButton.addEventListener("click", () => {
      colors.b = 80;
      colors.r = 80;
      colors.g = 80;
      updateMaterials();
    });

    // Orange
    orangeButton.addEventListener("click", () => {
      colors.b = 0;
      colors.r = 20;
      colors.g = 3;
      updateMaterials();
    });

    // Silver
    silverButton.addEventListener("click", () => {
      colors.b = 2;
      colors.r = 2;
      colors.g = 2;
      updateMaterials();
    });

    // Blue
    blueButton.addEventListener("click", () => {
      colors.b = 20;
      colors.r = 0;
      colors.g = 1;
      updateMaterials();
    });

    // Random
    pinkButton.addEventListener("click", () => {
      colors.b = Math.floor(Math.random() * 10);
      colors.r = Math.floor(Math.random() * 10);
      colors.g = Math.floor(Math.random() * 10);
      updateMaterials();
    });

    const tick = () => {
      // Set points positions

      views.forEach((view) => {
        view.camera.lookAt(cameraTarget);
        let bottom = sizes.height * view.bottom;
        let height = sizes.height * view.height;
        renderer.setViewport(0, 0, sizes.width, sizes.height);
        renderer.setScissor(0, bottom, sizes.width, height);
        renderer.setScissorTest(true);
        renderer.render(view.scene, view.camera);
      });

      for (const point of points) {
        const screenPosition = point.position.clone();
        screenPosition.project(views[0].camera);

        raycaster.setFromCamera(screenPosition, views[0].camera);
        const intersects = raycaster.intersectObjects(views[0].scene.children,
          true
        );
        if (intersects.length === 0) {
          point.element.classList.add("visible");
        } else {
          const intersectionDistance = intersects[0].distance;
          const pointsDistance = point.position.distanceTo(
            views[0].camera.position
          );
          point.element.classList.remove("visible");

          if (intersectionDistance < pointsDistance) {
            point.element.classList.remove("visible");
          } else {
            point.element.classList.add("visible");
          }
        }

        const translateX = screenPosition.x * sizes.width * 0.5;
        const translateY = -screenPosition.y * sizes.height * 0.5;
        point.element.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`;
      }
      // Render

      //   Controls
      controls.update();

      // Call tick again on the next frame
      window.requestAnimationFrame(tick);
    };
    tick();
  });
});
